import { URLS } from '@/lib/constants';
import { logger } from '../lib/logger';
import type { WPLiveSessionEvent } from '../types/live-session';

export function isProOnly(value: string | boolean | number | undefined): boolean {
  return value === true || value === '1' || value === 1;
}


 //Decode HTML entities from WordPress API for title/teacher_name.
 //Handles: &amp; &quot; &apos; &lt; &gt; | &#123; &#x7B; | &nbsp; &ndash; &mdash; &hellip; &lsquo; &rsquo; &ldquo; &rdquo; etc.

function decodeHtmlEntities(s: string): string {
  return s
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, '\u00A0')
    .replace(/&ndash;/g, '–')
    .replace(/&mdash;/g, '—')
    .replace(/&hellip;/g, '…')
    .replace(/&lsquo;/g, "'")
    .replace(/&rsquo;/g, "'")
    .replace(/&ldquo;/g, '"')
    .replace(/&rdquo;/g, '"')
    .replace(/&laquo;/g, '«')
    .replace(/&raquo;/g, '»')
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, code) => String.fromCharCode(parseInt(code, 16)));
}

function getBaseUrl(): string {
  const url = URLS.WORDPRESS_BASE;
  return url.replace(/\/+$/, '');
}

export interface FetchEventsResult {
  success: boolean;
  events: WPLiveSessionEvent[];
  error?: string;
}

export async function fetchLiveSessionEvents(): Promise<FetchEventsResult> {
  const baseUrl = getBaseUrl();
  // All platforms use the public REST endpoint: GET /wp-json/osj/v1/live-sessions.
  // It is anonymous and edge-cacheable, unlike the legacy wc-ajax POST, which is
  // uncacheable and re-renders the events server-side on every poll. The only
  // per-user fields the legacy endpoint added (RSVP `actions` HTML, cookies)
  // are not used anywhere in this app, so the payloads are equivalent.
  const url = `${baseUrl}/wp-json/osj/v1/live-sessions`;

  try {
    const response = await fetch(url, { method: 'GET' });

    if (!response.ok) {
      logger.warn('Live sessions fetch failed', { status: response.status, url });
      return { success: false, events: [], error: `HTTP ${response.status}` };
    }

    const json = await response.json();
    const rawEvents = json?.data?.events;

    if (!Array.isArray(rawEvents)) {
      return { success: true, events: [] };
    }

    const events = rawEvents
      .filter((e: unknown) => e && typeof e === 'object' && 'id' in e && 'title' in e)
      .map((e: any) => ({
        id: Number(e.id),
        title: decodeHtmlEntities(String(e.title ?? '')),
        url: String(e.url ?? ''),
        event_start_time: String(e.event_start_time ?? ''),
        event_start_timestamp: Number(e.event_start_timestamp ?? 0),
        event_end_time: e.event_end_time != null ? String(e.event_end_time) : undefined,
        event_end_timestamp: e.event_end_timestamp != null ? Number(e.event_end_timestamp) : undefined,
        event_timezone: e.event_timezone != null ? String(e.event_timezone) : undefined,
        display_event_timezone: e.display_event_timezone,
        teacher_name: decodeHtmlEntities(String(e.teacher_name ?? '')),
        teacher_avatar:
          typeof e.teacher_avatar === 'string' ? e.teacher_avatar : false,
        teacher_event_avatar:
          typeof e.teacher_event_avatar === 'string'
            ? e.teacher_event_avatar
            : e.teacher_event_avatar === false
              ? false
              : undefined,
        teacher_link: e.teacher_link,
        is_live_event: Boolean(e.is_live_event),
        is_only_for_pro: isProOnly(e.is_only_for_pro),
        is_canceled: Boolean(e.is_canceled),
        is_past_event: Boolean(e.is_past_event),
        is_recurring: e.is_recurring,
        actions: e.actions,
        attendees: e.attendees ?? null,
        event_start_midnight_timestamp: e.event_start_midnight_timestamp,
      })) as WPLiveSessionEvent[];

    return { success: true, events };
  } catch (err) {
    logger.error('Live sessions fetch error', { url, error: err });
    return { success: false, events: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
